import React, { useState, useContext, useEffect } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { 
  Settings as SettingsIcon, RefreshCw, Check, Copy,
  FileSpreadsheet, CheckCircle, AlertCircle,
  ExternalLink, Clock, Database, ArrowLeftRight, Code, ChevronDown, ChevronRight,
  Download, HardDrive, FileJson, Archive, Cloud, Link, Unlink, FolderSync
} from 'lucide-react';

function Settings() {
  const { API, user } = useContext(AppContext);
  const [activeModule, setActiveModule] = useState('production');
  const [syncing, setSyncing] = useState({});
  const [syncResults, setSyncResults] = useState({});
  const [expandedScript, setExpandedScript] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Data Export State
  const [exportSummary, setExportSummary] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState(null);
  
  // Google Drive Backup State
  const [driveStatus, setDriveStatus] = useState({ configured: false, connected: false });
  const [driveLoading, setDriveLoading] = useState(true);
  const [backupHistory, setBackupHistory] = useState([]);
  const [backupCollections, setBackupCollections] = useState([]);
  const [backingUp, setBackingUp] = useState(false);
  const [backupResult, setBackupResult] = useState(null);

  const modules = [
    { id: 'production', name: 'Production Data', icon: '🏭', description: 'Furnace production, batch numbers, recovery data' },
    { id: 'grn', name: 'GRN (Raw Material)', icon: '📦', description: 'Goods Receipt Notes, scrap inventory' },
    { id: 'dispatch', name: 'Dispatch', icon: '🚚', description: 'Product dispatch records' },
    { id: 'consumable-stock', name: 'Consumable Stock', icon: '📋', description: 'Store consumable inventory levels' },
    { id: 'master-data/raw-material/suppliers', name: 'Supplier Dashboard', icon: '🌍', description: 'Supplier/Party master data with locations' },
    { id: 'master-data/stores/vendors', name: 'Master: Vendors', icon: '🏢', description: 'Vendor master data' },
    { id: 'master-data/stores/commodities', name: 'Master: Commodities', icon: '📦', description: 'Commodity, HSN & GST master data' },
    { id: 'master-data/stores/terms_conditions', name: 'Master: Terms & Conditions', icon: '📄', description: 'Terms and conditions' },
    { id: 'master-data/stores/payment_terms', name: 'Master: Payment Terms', icon: '💳', description: 'Payment terms master data' },
    { id: 'master-data/stores/uom', name: 'Master: UOM', icon: '📏', description: 'Unit of measurement' },
    { id: 'master-data/stores/state_codes', name: 'Master: State Codes', icon: '🗺️', description: 'State codes for GST' }
  ];
  
  // Load export summary on mount
  useEffect(() => {
    loadExportSummary();
    loadDriveStatus();
  }, []);
  
  const loadDriveStatus = async () => {
    setDriveLoading(true);
    try {
      const [statusRes, historyRes, collectionsRes] = await Promise.all([
        axios.get(`${API}/backup/drive/status`),
        axios.get(`${API}/backup/history`),
        axios.get(`${API}/backup/collections`)
      ]);
      setDriveStatus(statusRes.data);
      setBackupHistory(historyRes.data.data || []);
      setBackupCollections(collectionsRes.data.collections || []);
    } catch (error) {
      console.error('Error loading drive status:', error);
    }
    setDriveLoading(false);
  };
  
  const connectGoogleDrive = async () => {
    try {
      const response = await axios.get(`${API}/backup/drive/connect`);
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to connect Google Drive');
    }
  };
  
  const disconnectGoogleDrive = async () => {
    if (!window.confirm('Are you sure you want to disconnect Google Drive?')) return;
    try {
      await axios.post(`${API}/backup/drive/disconnect`, {});
      loadDriveStatus();
    } catch (error) {
      alert('Failed to disconnect Google Drive');
    }
  };
  
  const runBackupNow = async () => {
    setBackingUp(true);
    setBackupResult(null);
    try {
      const response = await axios.post(`${API}/backup/now`, {});
      setBackupResult({ type: 'success', data: response.data });
      loadDriveStatus();
    } catch (error) {
      setBackupResult({ 
        type: 'error', 
        message: error.response?.data?.detail || 'Backup failed' 
      });
    }
    setBackingUp(false);
  };
  
  const loadExportSummary = async () => {
    try {
      const response = await axios.get(`${API}/export/summary`);
      setExportSummary(response.data);
    } catch (error) {
      console.error('Error loading export summary:', error);
    }
  };
  
  const handleExportAll = async () => {
    setExporting(true);
    setExportStatus('downloading');
    try {
      const response = await axios.get(`${API}/export/all`, {
        responseType: 'blob',
        
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0,10).replace(/-/g, '');
      link.setAttribute('download', `swift_strips_erp_backup_${timestamp}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setExportStatus('success');
      setTimeout(() => setExportStatus(null), 3000);
    } catch (error) {
      console.error('Export failed:', error);
      setExportStatus('error');
      setTimeout(() => setExportStatus(null), 3000);
    } finally {
      setExporting(false);
    }
  };
  
  const handleExportCollection = async (collectionName) => {
    try {
      const response = await axios.get(`${API}/export/collection/${collectionName}`, {
        responseType: 'blob',
        
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${collectionName}_${new Date().toISOString().slice(0,10)}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  // Google Apps Script code that user will paste into their sheet
  const getAppsScriptCode = () => {
    const baseUrl = API;
    return `// Swift Strips India - Google Sheets Sync Script
// Paste this code in: Extensions > Apps Script > Code.gs
// Then add triggers for automatic sync

const ERP_API_URL = "${baseUrl}";

// ========== MENU SETUP ==========
function onOpen() {
  SpreadsheetApp.getUi()
    .createMenu('🔄 ERP Sync')
    .addItem('⬇️ Pull from ERP', 'pullFromERP')
    .addItem('⬆️ Push to ERP', 'pushToERP')
    .addItem('🔄 Sync Both Ways', 'syncBidirectional')
    .addSeparator()
    .addItem('⚙️ Setup Headers', 'setupHeaders')
    .addToUi();
}

// ========== CONFIGURATION ==========
// Change this based on which module this sheet is for
// OPTIONS: 'production', 'grn', 'dispatch', 'consumable-stock', 
//          'master-data/raw-material/suppliers',
//          'master-data/stores/vendors', 'master-data/stores/commodities', 
//          'master-data/stores/terms_conditions', 'master-data/stores/payment_terms',
//          'master-data/stores/uom', 'master-data/stores/state_codes'
const MODULE = 'dispatch';

// Standard ERP headers
const HEADERS = {
  production: ['batchNumber', 'date', 'shift', 'furnace', 'operatorNames', 'scrapType', 'partyName', 'invoiceNo', 'inputWeight', 'totalInput', 'totalOutput', 'recoveryPercent', 'dross', 'drossPercent', 'burningLoss', 'burningLossPercent', 'fgType', 'status', 'syncedAt'],
  grn: ['grnNumber', 'invoiceNo', 'invoiceDate', 'receivedDate', 'partyName', 'partyGST', 'vehicleNo', 'driverName', 'scrapType', 'grossWeight', 'tareWeight', 'packingWeight', 'netWeight', 'rate', 'amount', 'result', 'status', 'remarks', 'syncedAt'],
  dispatch: ['dispatchId', 'date', 'customerName', 'productType', 'quantity', 'vehicleNo', 'driverName', 'invoiceNo', 'amount', 'status', 'syncedAt'],
  'consumable-stock': ['code', 'itemName', 'party', 'quantity', 'minStock', 'unit', 'rate', 'category', 'status', 'syncedAt'],
  // Supplier Dashboard
  'master-data/raw-material/suppliers': ['name', 'code', 'gst', 'contactName', 'contact', 'address', 'city', 'state', 'country', 'status'],
  // Master Data Tables
  'master-data/stores/vendors': ['vendorCode', 'vendorName', 'gstin', 'pan', 'contactPerson', 'phone', 'email', 'address', 'city', 'state', 'pincode', 'bankName', 'accountNo', 'ifscCode', 'status'],
  'master-data/stores/commodities': ['commodityCode', 'commodityName', 'category', 'hsnCode', 'gstRate', 'cgst', 'sgst', 'igst', 'uom', 'description'],
  'master-data/stores/terms_conditions': ['termCode', 'termType', 'termTitle', 'termDescription', 'applicableTo', 'priority', 'status'],
  'master-data/stores/payment_terms': ['paymentCode', 'paymentName', 'creditDays', 'discountPercent', 'discountDays', 'description', 'status'],
  'master-data/stores/uom': ['uomCode', 'uomName', 'uomType', 'baseUom', 'conversionFactor', 'description'],
  'master-data/stores/state_codes': ['stateCode', 'stateName', 'gstStateCode', 'country', 'zone']
};

// ========== COLUMN MAPPING FOR EXISTING SHEETS ==========
// Map YOUR column names to ERP field names
// Edit this if your sheet has different column names
const COLUMN_MAPPING = {
  dispatch: {
    'SL No.': 'dispatchId',
    'Invoice No.': 'invoiceNo', 
    'Date': 'date',
    'Name': 'customerName',
    'Items': 'productType',
    'Qty (MT)': 'quantity',
    'Vehicle No': 'vehicleNo',
    'Driver': 'driverName',
    'Amount': 'amount',
    'Status': 'status'
  },
  production: {
    'Batch No': 'batchNumber',
    'Date': 'date',
    'Shift': 'shift',
    'Furnace': 'furnace',
    'Operator': 'operatorNames',
    'Scrap Type': 'scrapType',
    'Party': 'partyName',
    'Invoice': 'invoiceNo',
    'Input (kg)': 'totalInput',
    'Output (kg)': 'totalOutput',
    'Recovery %': 'recoveryPercent',
    'FG Type': 'fgType'
  },
  grn: {
    'GRN No': 'grnNumber',
    'Invoice No': 'invoiceNo',
    'Invoice Date': 'invoiceDate',
    'Received Date': 'receivedDate',
    'Party Name': 'partyName',
    'Vehicle No': 'vehicleNo',
    'Scrap Type': 'scrapType',
    'Gross Wt': 'grossWeight',
    'Tare Wt': 'tareWeight',
    'Net Wt': 'netWeight',
    'Rate': 'rate',
    'Amount': 'amount',
    'Status': 'status'
  },
  'consumable-stock': {
    'Code': 'code',
    'Item Name': 'itemName',
    'Item': 'itemName',
    'Party': 'party',
    'Vendor': 'party',
    'Supplier': 'party',
    'Quantity': 'quantity',
    'Qty': 'quantity',
    'Stock': 'quantity',
    'Min Stock': 'minStock',
    'Minimum': 'minStock',
    'Reorder Level': 'minStock',
    'Unit': 'unit',
    'UOM': 'unit',
    'Rate': 'rate',
    'Price': 'rate',
    'Category': 'category'
  },
  // Master Data - Vendors
  'master-data/stores/vendors': {
    'Vendor Code': 'vendorCode',
    'Code': 'vendorCode',
    'Party Code': 'vendorCode',
    'Vendor Name': 'vendorName',
    'Name': 'vendorName',
    'Party Name': 'vendorName',
    'Contact Person': 'contactPerson',
    'Contact': 'contactPerson',
    'Contact Person 1': 'contactPerson',
    'Phone': 'phone',
    'Mobile': 'phone',
    'Email': 'email',
    'Email Id1': 'email',
    'GST No': 'gstin',
    'GSTIN': 'gstin',
    'GSTIN No': 'gstin',
    'GST': 'gstin',
    'PAN No': 'pan',
    'PAN': 'pan',
    'Address': 'address',
    'Address Description': 'address',
    'City': 'city',
    'State': 'state',
    'Pincode': 'pincode',
    'PIN': 'pincode',
    'Bank Name': 'bankName',
    'Bank': 'bankName',
    'Account No': 'accountNo',
    'A/C No': 'accountNo',
    'IFSC Code': 'ifscCode',
    'IFSC': 'ifscCode',
    'Status': 'status'
  },
  // Master Data - Commodities
  'master-data/stores/commodities': {
    'Commodity Code': 'commodityCode',
    'Code': 'commodityCode',
    'Commodity Name': 'commodityName',
    'Name': 'commodityName',
    'Commodity': 'commodityName',
    'Category': 'category',
    'HSN Code': 'hsnCode',
    'HSN': 'hsnCode',
    'GST Rate': 'gstRate',
    'GST %': 'gstRate',
    'CGST': 'cgst',
    'CGST %': 'cgst',
    'SGST': 'sgst',
    'SGST %': 'sgst',
    'IGST': 'igst',
    'IGST %': 'igst',
    'UOM': 'uom',
    'Unit': 'uom',
    'Description': 'description'
  },
  // Master Data - Terms & Conditions
  'master-data/stores/terms_conditions': {
    'Term Code': 'termCode',
    'Code': 'termCode',
    'Term Type': 'termType',
    'Type': 'termType',
    'Title': 'termTitle',
    'Term Title': 'termTitle',
    'Description': 'termDescription',
    'Term Description': 'termDescription',
    'Applicable To': 'applicableTo',
    'Priority': 'priority',
    'Status': 'status'
  },
  // Master Data - Payment Terms
  'master-data/stores/payment_terms': {
    'Payment Code': 'paymentCode',
    'Code': 'paymentCode',
    'Payment Name': 'paymentName',
    'Name': 'paymentName',
    'Credit Days': 'creditDays',
    'Days': 'creditDays',
    'Discount %': 'discountPercent',
    'Discount Percent': 'discountPercent',
    'Discount Days': 'discountDays',
    'Description': 'description',
    'Status': 'status'
  },
  // Master Data - UOM
  'master-data/stores/uom': {
    'UOM Code': 'uomCode',
    'Code': 'uomCode',
    'UOM Name': 'uomName',
    'Name': 'uomName',
    'UOM': 'uomName',
    'Type': 'uomType',
    'UOM Type': 'uomType',
    'Base UOM': 'baseUom',
    'Base': 'baseUom',
    'Conversion Factor': 'conversionFactor',
    'Factor': 'conversionFactor',
    'Description': 'description'
  },
  // Master Data - State Codes
  'master-data/stores/state_codes': {
    'State Code': 'stateCode',
    'Code': 'stateCode',
    'State Name': 'stateName',
    'State': 'stateName',
    'Name': 'stateName',
    'GST State Code': 'gstStateCode',
    'GST Code': 'gstStateCode',
    'Country': 'country',
    'Zone': 'zone',
    'Region': 'zone'
  },
  // Supplier Dashboard (Raw Material)
  'master-data/raw-material/suppliers': {
    'Party Name': 'name',
    'Name': 'name',
    'Supplier Name': 'name',
    'PARTY NAME': 'name',
    'Party Code': 'code',
    'Code': 'code',
    'PARTY CODE': 'code',
    'GST No': 'gst',
    'GST': 'gst',
    'GSTIN': 'gst',
    'GST NO': 'gst',
    'Contact Name': 'contactName',
    'CONTACT NAME': 'contactName',
    'Contact Person': 'contactName',
    'CONTACT PERSON': 'contactName',
    'Contact': 'contact',
    'Contact Number': 'contact',
    'Phone': 'contact',
    'Mobile': 'contact',
    'CONTACT': 'contact',
    'CONTACT NUMBER': 'contact',
    'Address': 'address',
    'ADDRESS': 'address',
    'City': 'city',
    'CITY': 'city',
    'State': 'state',
    'STATE': 'state',
    'Country': 'country',
    'COUNTRY': 'country',
    'Status': 'status',
    'STATUS': 'status'
  }
};

// ========== PULL DATA FROM ERP ==========
function pullFromERP() {
  const ui = SpreadsheetApp.getUi();
  const sheet = SpreadsheetApp.getActiveSpreadsheet().getActiveSheet();
  
  try {
    ui.showModalDialog(HtmlService.createHtmlOutput('<p>Fetching data from ERP...</p>').setWidth(200).setHeight(100), 'Please wait');
    
    const response = UrlFetchApp.fetch(ERP_API_URL + '/sync/' + MODULE, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    
    const result = JSON.parse(response.getContentText());
    const data = result.data || [];
    
    if (data.length === 0) {
      ui.alert('No data found in ERP for ' + MODULE);
      return;
    }
    
    // Clear existing data (keep headers)
    const lastRow = sheet.getLastRow();
    if (lastRow > 1) {
      sheet.getRange(2, 1, lastRow - 1, sheet.getLastColumn()).clearContent();
    }
    
    // Setup headers if not present
    const headers = HEADERS[MODULE];
    sheet.getRange(1, 1, 1, headers.length).setValues([headers]);
    
    // Write data
    const rows = data.map(record => headers.map(h => record[h] || ''));
    if (rows.length > 0) {
      sheet.getRange(2, 1, rows.length, headers.length).setValues(rows);
    }
    
    ui.alert('Success! Pulled ' + data.length + ' records from ERP.');
    
  } catch (error) {
    ui.alert('Error: ' + error.message);
  }
}

// ========== PUSH DATA TO ERP (with column mapping) ==========
function pushToERP() {
  const ui = SpreadsheetApp.getUi();
  const sheet = SpreadsheetApp.getActiveSpreadsheet().getActiveSheet();
  
  try {
    const lastRow = sheet.getLastRow();
    const lastCol = sheet.getLastColumn();
    
    // Find header row by looking for known column names
    let headerRow = 0;
    let headers = [];
    const knownHeaders = ['INV NO', 'INV_NO', 'DATE', 'PARTY', 'PRODUCT', 'INV QTY', 'VEHICLE NO', 'SL No.', 'Invoice No.', 'Date', 'Name', 'Items', 'Qty', 'dispatchId', 'invoiceNo', 'customerName'];
    
    for (let r = 1; r <= Math.min(10, lastRow); r++) {
      const testRow = sheet.getRange(r, 1, 1, lastCol).getValues()[0];
      const nonEmpty = testRow.filter(h => h !== '');
      
      // Check if this row contains known headers
      const hasKnownHeader = testRow.some(cell => 
        knownHeaders.some(kh => String(cell).toUpperCase() === kh.toUpperCase())
      );
      
      if (hasKnownHeader) {
        headerRow = r;
        headers = testRow;
        break;
      }
    }
    
    if (headerRow === 0) {
      ui.alert('Could not find header row. Make sure your sheet has column headers like "Invoice No.", "Date", "Name", etc.');
      return;
    }
    
    const dataStartRow = headerRow + 1;
    if (lastRow < dataStartRow) {
      ui.alert('No data to push. Add some rows first.');
      return;
    }
    
    const dataRange = sheet.getRange(dataStartRow, 1, lastRow - headerRow, headers.length);
    const values = dataRange.getValues();
    
    // Get column mapping for this module
    const mapping = COLUMN_MAPPING[MODULE] || {};
    
    // Convert rows to objects with mapped field names
    const data = values.filter(row => row.some(cell => cell !== '')).map((row, idx) => {
      const obj = {};
      headers.forEach((header, i) => {
        if (header && row[i] !== '') {
          // Use mapping if available, otherwise use header as-is
          const erpField = mapping[header] || header;
          let value = row[i];
          
          // Format dates
          if (value instanceof Date) {
            value = Utilities.formatDate(value, Session.getScriptTimeZone(), 'yyyy-MM-dd');
          }
          
          obj[erpField] = value;
        }
      });
      
      // Generate dispatchId if not present (for dispatch module)
      if (MODULE === 'dispatch' && !obj.dispatchId) {
        obj.dispatchId = obj.invoiceNo || ('DSP-' + (idx + 1));
      }
      
      return obj;
    });
    
    if (data.length === 0) {
      ui.alert('No valid data to push.');
      return;
    }
    
    // Show debug info
    ui.showModalDialog(
      HtmlService.createHtmlOutput(
        '<p>Found headers at row ' + headerRow + '</p>' +
        '<p>Headers: ' + headers.filter(h => h).join(', ') + '</p>' +
        '<p>Pushing ' + data.length + ' records to ERP...</p>'
      ).setWidth(350).setHeight(150), 
      'Please wait'
    );
    
    const response = UrlFetchApp.fetch(ERP_API_URL + '/sync/' + MODULE, {
      method: 'POST',
      contentType: 'application/json',
      payload: JSON.stringify(data)
    });
    
    const result = JSON.parse(response.getContentText());
    ui.alert('Success!\\nImported: ' + result.imported + '\\nUpdated: ' + result.updated);
    
  } catch (error) {
    ui.alert('Error: ' + error.message);
  }
}

// ========== BI-DIRECTIONAL SYNC ==========
function syncBidirectional() {
  pullFromERP();
  // Data from sheets gets merged when you pull, since ERP is source of truth
  // If you have local edits, push them first before pulling
}

// ========== SETUP HEADERS ==========
function setupHeaders() {
  const sheet = SpreadsheetApp.getActiveSpreadsheet().getActiveSheet();
  const headers = HEADERS[MODULE];
  
  // Set headers in row 1
  sheet.getRange(1, 1, 1, headers.length).setValues([headers]);
  
  // Format header row
  const headerRange = sheet.getRange(1, 1, 1, headers.length);
  headerRange.setBackground('#1e3a5f');
  headerRange.setFontColor('#ffffff');
  headerRange.setFontWeight('bold');
  
  SpreadsheetApp.getUi().alert('Headers set up for ' + MODULE + ' module!');
}

// ========== AUTOMATIC SYNC (Optional) ==========
// To enable automatic sync, go to: Triggers (clock icon) > Add Trigger
// Choose: pullFromERP, Time-driven, Hour timer, Every hour

function autoSync() {
  pullFromERP();
}
`;
  };

  const copyScriptToClipboard = () => {
    navigator.clipboard.writeText(getAppsScriptCode());
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  // Manual sync buttons that work without Google Sheets (for testing)
  const handleManualSync = async (module, direction) => {
    setSyncing(prev => ({ ...prev, [module]: direction }));
    
    try {
      let response;
      if (direction === 'pull') {
        response = await axios.get(`${API}/sync/${module}`);
        setSyncResults(prev => ({
          ...prev,
          [module]: { 
            success: true, 
            message: `Found ${response.data.count} records ready for sync`,
            time: new Date().toLocaleTimeString()
          }
        }));
      } else {
        // For push, this would need data - just show status
        response = await axios.get(`${API}/sync/${module}`);
        setSyncResults(prev => ({
          ...prev,
          [module]: { 
            success: true, 
            message: `${response.data.count} records in ERP database`,
            time: new Date().toLocaleTimeString()
          }
        }));
      }
    } catch (error) {
      console.error('Sync error:', error);
      setSyncResults(prev => ({
        ...prev,
        [module]: { 
          success: false, 
          message: error.response?.data?.detail || 'Sync check failed',
          time: new Date().toLocaleTimeString()
        }
      }));
    }
    
    setSyncing(prev => ({ ...prev, [module]: null }));
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center gap-3 bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="p-2 bg-gradient-to-br from-gray-700 to-gray-800 rounded-lg">
          <SettingsIcon className="text-white" size={24} />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Google Sheets Sync</h1>
          <p className="text-sm text-gray-500">Bi-directional sync using Google Apps Script</p>
        </div>
      </div>

      {/* How It Works */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-5">
        <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
          <FileSpreadsheet className="text-blue-600" size={20} />
          How Google Sheets Sync Works
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="bg-white/70 rounded-lg p-3">
            <div className="font-semibold text-blue-700 mb-1">1. Copy Script</div>
            <p className="text-gray-600">Copy the Apps Script code below and paste it into your Google Sheet</p>
          </div>
          <div className="bg-white/70 rounded-lg p-3">
            <div className="font-semibold text-blue-700 mb-1">2. Configure Module</div>
            <p className="text-gray-600">Change the MODULE variable to match your sheet (production, grn, or dispatch)</p>
          </div>
          <div className="bg-white/70 rounded-lg p-3">
            <div className="font-semibold text-blue-700 mb-1">3. Sync Data</div>
            <p className="text-gray-600">Use the "ERP Sync" menu in your sheet to pull/push data</p>
          </div>
        </div>
      </div>

      {/* Apps Script Code Section */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div 
          className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between cursor-pointer hover:bg-gray-100 transition"
          onClick={() => setExpandedScript(!expandedScript)}
        >
          <div className="flex items-center gap-3">
            <Code className="text-green-600" size={20} />
            <div>
              <h3 className="font-bold text-gray-900">Google Apps Script Code</h3>
              <p className="text-xs text-gray-500">Click to {expandedScript ? 'collapse' : 'expand'} the script</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); copyScriptToClipboard(); }}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                copied ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
              data-testid="copy-script-btn"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Copied!' : 'Copy Script'}
            </button>
            {expandedScript ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </div>
        </div>

        {expandedScript && (
          <div className="p-4">
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto max-h-96 overflow-y-auto">
              <code>{getAppsScriptCode()}</code>
            </pre>
          </div>
        )}
      </div>

      {/* Setup Instructions */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
          <AlertCircle className="text-amber-500" size={18} />
          Step-by-Step Setup Instructions
        </h3>
        
        <ol className="space-y-4 text-sm">
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-bold text-xs">1</span>
            <div>
              <p className="font-medium text-gray-800">Open your Google Sheet</p>
              <p className="text-gray-500">Create a new sheet or open an existing one for Production, GRN, or Dispatch data</p>
            </div>
          </li>
          
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-bold text-xs">2</span>
            <div>
              <p className="font-medium text-gray-800">Open Apps Script Editor</p>
              <p className="text-gray-500">Go to <strong>Extensions → Apps Script</strong> in your Google Sheet menu</p>
            </div>
          </li>
          
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-bold text-xs">3</span>
            <div>
              <p className="font-medium text-gray-800">Paste the Script</p>
              <p className="text-gray-500">Delete any existing code and paste the script from above. Click the <strong>Copy Script</strong> button.</p>
            </div>
          </li>
          
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-bold text-xs">4</span>
            <div>
              <p className="font-medium text-gray-800">Set the Module Type</p>
              <p className="text-gray-500">Find line <code className="bg-gray-100 px-1 rounded">const MODULE = 'dispatch';</code> and change to your module:</p>
              <ul className="mt-1 text-xs text-gray-500 space-y-0.5 ml-2">
                <li>• <code className="bg-gray-100 px-1 rounded">'dispatch'</code> - Dispatch records</li>
                <li>• <code className="bg-gray-100 px-1 rounded">'master-data/raw-material/suppliers'</code> - <strong>Supplier Dashboard</strong></li>
                <li>• <code className="bg-gray-100 px-1 rounded">'master-data/stores/vendors'</code> - Vendors</li>
                <li>• <code className="bg-gray-100 px-1 rounded">'production'</code>, <code className="bg-gray-100 px-1 rounded">'grn'</code>, etc.</li>
              </ul>
            </div>
          </li>
          
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-bold text-xs">5</span>
            <div>
              <p className="font-medium text-gray-800">Save and Authorize</p>
              <p className="text-gray-500">Click <strong>Save</strong> (disk icon), then <strong>Run → onOpen</strong>. Authorize the script when prompted.</p>
            </div>
          </li>
          
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-bold text-xs">6</span>
            <div>
              <p className="font-medium text-gray-800">Refresh and Use</p>
              <p className="text-gray-500">Refresh your Google Sheet. You'll see a new <strong>"🔄 ERP Sync"</strong> menu. Use it to sync data!</p>
            </div>
          </li>
        </ol>
        
        <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
          <p className="text-green-800 text-xs">
            <strong>Pro Tip:</strong> For automatic hourly sync, go to Apps Script → Triggers (clock icon) → Add Trigger → Select "autoSync" function with "Hour timer"
          </p>
        </div>
      </div>

      {/* Module Data Status */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <Database size={18} className="text-purple-600" />
            ERP Data Status
          </h3>
          <p className="text-sm text-gray-600">Check available data for each module</p>
        </div>

        <div className="divide-y divide-gray-100">
          {modules.map(module => {
            const result = syncResults[module.id];
            const isSyncing = syncing[module.id];
            
            return (
              <div key={module.id} className="p-4 hover:bg-gray-50 transition">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{module.icon}</span>
                    <div>
                      <h4 className="font-medium text-gray-900">{module.name}</h4>
                      <p className="text-xs text-gray-500">{module.description}</p>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleManualSync(module.id, 'pull')}
                    disabled={isSyncing}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                      isSyncing ? 'bg-blue-100 text-blue-600' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                    }`}
                    data-testid={`check-${module.id}-btn`}
                  >
                    {isSyncing ? (
                      <RefreshCw size={12} className="animate-spin" />
                    ) : (
                      <Database size={12} />
                    )}
                    Check Data
                  </button>
                </div>
                
                {result && (
                  <div className={`mt-2 p-2 rounded-lg text-xs flex items-center gap-2 ${
                    result.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                  }`}>
                    {result.success ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                    {result.message}
                    <span className="text-gray-400 ml-auto">
                      <Clock size={10} className="inline mr-1" />
                      {result.time}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* API Endpoints Reference */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <ExternalLink size={16} />
          API Endpoints (for reference)
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs font-mono">
          <div className="bg-white p-2 rounded border">
            <span className="text-green-600">GET</span> /api/sync/production
          </div>
          <div className="bg-white p-2 rounded border">
            <span className="text-green-600">GET</span> /api/sync/grn
          </div>
          <div className="bg-white p-2 rounded border">
            <span className="text-green-600">GET</span> /api/sync/dispatch
          </div>
          <div className="bg-white p-2 rounded border">
            <span className="text-blue-600">POST</span> /api/sync/production
          </div>
          <div className="bg-white p-2 rounded border">
            <span className="text-blue-600">POST</span> /api/sync/grn
          </div>
          <div className="bg-white p-2 rounded border">
            <span className="text-blue-600">POST</span> /api/sync/dispatch
          </div>
        </div>
      </div>

      {/* Troubleshooting */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <h4 className="font-semibold text-amber-800 mb-2">Troubleshooting</h4>
        <ul className="text-xs text-amber-700 space-y-1 list-disc list-inside">
          <li>If the "ERP Sync" menu doesn't appear, refresh the page after saving the script</li>
          <li>If you get an authorization error, run the script once from the Apps Script editor first</li>
          <li>Make sure the MODULE variable matches your sheet type (production, grn, dispatch, or consumable-stock)</li>
          <li>For CORS errors, contact support - the API may need configuration</li>
        </ul>
      </div>
      
      {/* ========== DATA EXPORT & BACKUP SECTION ========== */}
      <div className="mt-8 border-t border-gray-200 pt-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg">
            <HardDrive className="text-white" size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Data Export & Backup</h2>
            <p className="text-sm text-gray-500">Download your ERP data as JSON files for backup</p>
          </div>
        </div>
        
        {/* Export All Button */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <Archive size={20} className="text-blue-600" />
                Export All Data (ZIP)
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Download a complete backup of all ERP data as a ZIP file containing JSON files
              </p>
              {exportSummary && (
                <p className="text-xs text-blue-600 mt-2">
                  {exportSummary.collections?.length || 0} collections • {exportSummary.totalRecords?.toLocaleString() || 0} total records
                </p>
              )}
            </div>
            <button
              onClick={handleExportAll}
              disabled={exporting}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition shadow-md ${
                exportStatus === 'success' 
                  ? 'bg-green-500 text-white'
                  : exportStatus === 'error'
                  ? 'bg-red-500 text-white'
                  : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              data-testid="export-all-btn"
            >
              {exporting ? (
                <>
                  <RefreshCw size={18} className="animate-spin" />
                  Exporting...
                </>
              ) : exportStatus === 'success' ? (
                <>
                  <CheckCircle size={18} />
                  Downloaded!
                </>
              ) : exportStatus === 'error' ? (
                <>
                  <AlertCircle size={18} />
                  Failed
                </>
              ) : (
                <>
                  <Download size={18} />
                  Download Full Backup
                </>
              )}
            </button>
          </div>
        </div>
        
        {/* Individual Collections */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <FileJson size={18} className="text-gray-600" />
              Export Individual Collections
            </h3>
            <p className="text-xs text-gray-500 mt-1">Download specific data as separate JSON files</p>
          </div>
          
          <div className="max-h-80 overflow-y-auto">
            {exportSummary?.collections ? (
              <table className="w-full text-xs">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Collection</th>
                    <th className="px-4 py-2 text-right font-semibold text-gray-700">Records</th>
                    <th className="px-4 py-2 text-center font-semibold text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {exportSummary.collections.filter(c => c.recordCount > 0).map((coll) => (
                    <tr key={coll.collection} className="hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <div>
                          <p className="font-medium text-gray-900">{coll.displayName}</p>
                          <p className="text-gray-400 text-[10px] font-mono">{coll.collection}</p>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right font-semibold text-gray-700">
                        {coll.recordCount.toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-center">
                        <button
                          onClick={() => handleExportCollection(coll.collection)}
                          className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-[10px] font-medium"
                        >
                          <Download size={12} className="inline mr-1" />
                          JSON
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-8 text-center text-gray-500">
                <RefreshCw size={24} className="animate-spin mx-auto mb-2 text-gray-400" />
                Loading collections...
              </div>
            )}
          </div>
        </div>
        
        {/* Google Drive Info */}
        <div className="mt-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
          <h4 className="font-bold text-green-800 mb-4 flex items-center gap-2 text-lg">
            <Cloud size={22} />
            Google Drive Backup
          </h4>
          
          {driveLoading ? (
            <div className="flex items-center gap-2 text-gray-500">
              <RefreshCw className="animate-spin" size={16} />
              Loading Google Drive status...
            </div>
          ) : !driveStatus.configured ? (
            <div className="bg-white rounded-lg p-4 border border-yellow-200">
              <p className="text-sm text-yellow-700 mb-3">
                <AlertCircle size={16} className="inline mr-2" />
                Google Drive OAuth is not configured. To enable automatic backups:
              </p>
              <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside ml-4">
                <li>Create OAuth credentials in Google Cloud Console</li>
                <li>Add GOOGLE_DRIVE_CLIENT_ID, GOOGLE_DRIVE_CLIENT_SECRET to backend .env</li>
                <li>Set GOOGLE_DRIVE_REDIRECT_URI to your callback URL</li>
              </ol>
            </div>
          ) : driveStatus.connected ? (
            <div className="space-y-4">
              {/* Connected Status */}
              <div className="flex items-center justify-between bg-white rounded-lg p-4 border border-green-300">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle size={20} className="text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-green-700">Connected to Google Drive</p>
                    <p className="text-xs text-gray-500">{driveStatus.email}</p>
                  </div>
                </div>
                <button
                  onClick={disconnectGoogleDrive}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition"
                >
                  <Unlink size={14} />
                  Disconnect
                </button>
              </div>
              
              {/* Backup Now Button */}
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h5 className="font-semibold text-gray-900 flex items-center gap-2">
                      <FolderSync size={18} className="text-blue-600" />
                      Run Backup Now
                    </h5>
                    <p className="text-xs text-gray-500 mt-1">
                      Backup all ERP data to your Google Drive folder
                    </p>
                    {backupCollections.length > 0 && (
                      <p className="text-xs text-blue-600 mt-1">
                        {backupCollections.length} collections • {backupCollections.reduce((acc, c) => acc + c.records, 0).toLocaleString()} records
                      </p>
                    )}
                  </div>
                  <button
                    onClick={runBackupNow}
                    disabled={backingUp}
                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition font-medium disabled:opacity-50"
                  >
                    {backingUp ? (
                      <>
                        <RefreshCw size={16} className="animate-spin" />
                        Backing up...
                      </>
                    ) : (
                      <>
                        <Cloud size={16} />
                        Backup to Drive
                      </>
                    )}
                  </button>
                </div>
                
                {/* Backup Result */}
                {backupResult && (
                  <div className={`mt-4 p-3 rounded-lg ${
                    backupResult.type === 'success' 
                      ? 'bg-green-100 border border-green-300' 
                      : 'bg-red-100 border border-red-300'
                  }`}>
                    {backupResult.type === 'success' ? (
                      <div>
                        <p className="font-semibold text-green-700 flex items-center gap-2">
                          <CheckCircle size={16} />
                          {backupResult.data.message}
                        </p>
                        <a
                          href={backupResult.data.folderLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 mt-2 text-sm text-green-600 hover:underline"
                        >
                          <ExternalLink size={14} />
                          Open in Google Drive
                        </a>
                      </div>
                    ) : (
                      <p className="text-red-700 flex items-center gap-2">
                        <AlertCircle size={16} />
                        {backupResult.message}
                      </p>
                    )}
                  </div>
                )}
              </div>
              
              {/* Backup History */}
              {backupHistory.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="p-3 bg-gray-50 border-b border-gray-200">
                    <h5 className="font-semibold text-gray-900 text-sm">Recent Backups</h5>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {backupHistory.slice(0, 5).map((backup) => (
                      <div key={backup.id} className="flex items-center justify-between p-3 border-b border-gray-100 last:border-0 hover:bg-gray-50">
                        <div>
                          <p className="font-medium text-sm text-gray-900">{backup.folderName}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(backup.createdAt).toLocaleString()} • {backup.totalRecords?.toLocaleString()} records
                          </p>
                        </div>
                        <a
                          href={backup.folderLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                        >
                          <ExternalLink size={12} className="inline mr-1" />
                          View
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-700">
                Connect your Google Drive to enable automatic backups of your ERP data.
              </p>
              <button
                onClick={connectGoogleDrive}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition font-medium"
              >
                <Link size={16} />
                Connect Google Drive
              </button>
            </div>
          )}
        </div>
        
        {/* Backup Tips */}
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <h4 className="font-semibold text-blue-800 mb-2">Backup Best Practices</h4>
          <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
            <li>Download a full backup at least once a week</li>
            <li>Store backups in multiple locations (Google Drive, local computer, external drive)</li>
            <li>The ZIP file contains JSON files that can be imported back if needed</li>
            <li>Each JSON file includes collection name, export date, and record count</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default Settings;
